import { useState } from "react";

// STYLES
import * as Style from "./AccountDeletionStyle";

// LIBRARIES
import { useNavigate } from "react-router-dom";

// MISC
import SupIcon from "assets/LoginAssets/LOGO.svg";
import { ButtonTypes, InputTypes } from "models/GeneralTypes/generalTypes";
import { IStartAccountDeletionPayload, IFinishAccountDeletionPayload } from "models/interfaces";
import { AccountDeletionSteps, AccountDeletionStep } from "./AccountDeletionModel";

// REDUX
import { startAccountDeletion, finishAccountDeletion } from "api/accountDeletionApi";
import { useAppDispatch } from "hooks/reduxHooks";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import InputAtom from "components/Atoms/InputAtom/InputAtom";

const AccountDeletion = () => {
  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // CONSTANTS USING HOOKS
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<AccountDeletionStep>(AccountDeletionSteps.PHONE_INPUT);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // HANDLERS FUNCTIONS
  const handleSendOtp = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsLoading(true);
    setError(null);

    const payload: IStartAccountDeletionPayload = {
      phoneNumber: phoneNumber.trim()
    };

    try {
      await dispatch(startAccountDeletion(payload)).unwrap();
      setCurrentStep(AccountDeletionSteps.OTP_VERIFICATION);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setError("Failed to send OTP. Please check your phone number and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError("Please enter the OTP code");
      return;
    }

    setIsLoading(true);
    setError(null);

    const payload: IFinishAccountDeletionPayload = {
      phoneNumber: phoneNumber.trim(),
      otp: otp.trim()
    };

    try {
      await dispatch(finishAccountDeletion(payload)).unwrap();
      setCurrentStep(AccountDeletionSteps.SUCCESS);
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError("Invalid OTP code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPhoneInput = () => {
    setCurrentStep(AccountDeletionSteps.PHONE_INPUT);
    setOtp("");
    setError(null);
  };

  const handleGoToLogin = () => {
    navigate("/");
  };

  const renderPhoneInputStep = () => (
    <>
      <Style.StepIndicator>
        <Style.StepText>Step 1 of 2</Style.StepText>
      </Style.StepIndicator>

      <Style.Title>Delete Account</Style.Title>
      <Style.Subtitle>
        Enter your phone number to start the account deletion process.
        You will receive an OTP code to verify your identity.
      </Style.Subtitle>

      <Style.CenteredElementsContainer>
        <Style.InputContainer>
          <InputAtom
            name="phoneNumber"
            inputValue={phoneNumber}
            setInputValue={setPhoneNumber}
            type={InputTypes.LOGIN_INPUT}
            placeholder="Phone Number"
          />
        </Style.InputContainer>

        {error && <Style.ErrorMessage>{error}</Style.ErrorMessage>}
      </Style.CenteredElementsContainer>

      <Style.ButtonContainer>
        <ButtonAtom
          text={isLoading ? "Sending OTP..." : "Send OTP"}
          onClick={handleSendOtp}
          type={ButtonTypes.MAIN_BUTTON}
        />
        <Style.BackButton onClick={handleGoToLogin}>
          Back to Login
        </Style.BackButton>
      </Style.ButtonContainer>
    </>
  );

  const renderOtpVerificationStep = () => (
    <>
      <Style.StepIndicator>
        <Style.StepText>Step 2 of 2</Style.StepText>
      </Style.StepIndicator>

      <Style.Title>Verify OTP</Style.Title>
      <Style.Subtitle>
        We've sent a verification code to {phoneNumber}.
        Please enter the code below to confirm account deletion.
      </Style.Subtitle>

      <Style.CenteredElementsContainer>
        <Style.InputContainer>
          <InputAtom
            name="otp"
            inputValue={otp}
            setInputValue={setOtp}
            type={InputTypes.LOGIN_INPUT}
            placeholder="Enter OTP Code"
          />
        </Style.InputContainer>

        {error && <Style.ErrorMessage>{error}</Style.ErrorMessage>}
      </Style.CenteredElementsContainer>

      <Style.ButtonContainer>
        <ButtonAtom
          text={isLoading ? "Verifying..." : "Delete Account"}
          onClick={handleVerifyOtp}
          type={ButtonTypes.MAIN_BUTTON}
        />
        <Style.BackButton onClick={handleBackToPhoneInput}>
          Back to Phone Input
        </Style.BackButton>
      </Style.ButtonContainer>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <Style.Title>Account Deleted</Style.Title>
      <Style.Subtitle>
        Your account has been successfully deleted. All your data has been removed from our system.
      </Style.Subtitle>

      <Style.SuccessMessage>
        Account deletion completed successfully.
      </Style.SuccessMessage>

      <Style.ButtonContainer>
        <ButtonAtom
          text="Back to Login"
          onClick={handleGoToLogin}
          type={ButtonTypes.MAIN_BUTTON}
        />
      </Style.ButtonContainer>
    </>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case AccountDeletionSteps.PHONE_INPUT:
        return renderPhoneInputStep();
      case AccountDeletionSteps.OTP_VERIFICATION:
        return renderOtpVerificationStep();
      case AccountDeletionSteps.SUCCESS:
        return renderSuccessStep();
      default:
        return renderPhoneInputStep();
    }
  };

  return (
    <Style.AccountDeletionContainer>
      <Style.FormWrapper>
        <Style.SupIconImage src={SupIcon} />
        <Style.ContentContainer>
          {renderCurrentStep()}
        </Style.ContentContainer>
      </Style.FormWrapper>
    </Style.AccountDeletionContainer>
  );
};

export default AccountDeletion;